import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useSearchParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ChatProvider } from "@/context/ChatContext";
import { FavoritesProvider } from "@/context/FavoritesContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import { SiteContentProvider } from "@/context/SiteContentContext";
import { GlobalSiteBanner } from "@/components/GlobalSiteBanner";
import { ChatDock } from "@/components/ChatWindow";
import { SupportChatbot } from "@/components/SupportChatbot";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Home from "@/pages/Home";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import SellerAuth from "@/pages/SellerAuth";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import NewListing from "@/pages/NewListing";
import Pricing from "@/pages/Pricing";
import Credits from "@/pages/Credits";
import Services from "@/pages/Services";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import PaymentSuccess from "@/pages/PaymentSuccess";
import GoogleCallback from "@/pages/GoogleCallback";
import CarDetail from "@/pages/CarDetail";
import Favorites from "@/pages/Favorites";
import SellerPublicProfile from "@/pages/SellerPublicProfile";
import { SiteVisitHeartbeat } from "@/components/SiteVisitHeartbeat";
import { CookieConsentProvider } from "@/context/CookieConsentContext";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";
import { ErrorBoundary } from "@/components/ErrorBoundary";

function SilentErrorBoundary({ children }) {
  return (
    <ErrorBoundary
      fallback={
        <div className="fixed bottom-4 right-4 z-[40] text-xs text-slate-500 pointer-events-none">
          Chat betöltési hiba
        </div>
      }
    >
      {children}
    </ErrorBoundary>
  );
}

function AppShell() {
  return (
    <>
      <SiteVisitHeartbeat />
      <CookieConsentBanner />
      <SiteContentProvider>
        <GlobalSiteBanner />
        <FavoritesProvider>
          <ChatProvider>
            <AppRoutes />
            <SilentErrorBoundary>
              <ChatDock />
              <SupportChatbot />
            </SilentErrorBoundary>
          </ChatProvider>
        </FavoritesProvider>
      </SiteContentProvider>
    </>
  );
}

function AppRoutes() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const oauthQuery = searchParams.has("auth_token");
  if (
    location.pathname === "/google/vissza"
    || location.pathname === "/apple/vissza"
    || oauthQuery
    || location.hash?.includes("session_id=")
    || location.hash?.includes("auth_token=")
  ) {
    return <GoogleCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/auto/:id" element={<CarDetail />} />
      <Route path="/kereskedes/:slug" element={<SellerPublicProfile />} />
      <Route path="/kedvencek" element={<Favorites />} />
      <Route path="/belepes" element={<SellerAuth />} />
      <Route path="/belepes/jelszo-elfelejtve" element={<ForgotPassword />} />
      <Route path="/jelszo-visszaallitas" element={<ResetPassword />} />
      <Route path="/google/vissza" element={<GoogleCallback />} />
      <Route path="/apple/vissza" element={<GoogleCallback />} />
      <Route path="/arazas" element={<Pricing />} />
      <Route path="/kreditek" element={<Credits />} />
      <Route path="/szolgaltatasok" element={<Services />} />
      <Route path="/aszf" element={<Terms />} />
      <Route path="/adatvedelem" element={<Privacy />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/fiok" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/fiok/uj-hirdetes" element={<ProtectedRoute><NewListing /></ProtectedRoute>} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminDashboard />} />
    </Routes>
  );
}

function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster position="top-right" theme={theme} />;
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <ErrorBoundary>
              <CookieConsentProvider>
                <AppShell />
              </CookieConsentProvider>
            </ErrorBoundary>
          </BrowserRouter>
          <ThemedToaster />
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
